import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Header } from '../../components/header/Header';
import { MobileFooter } from '../../components/mobileFooter/MobileFooter';
import './Blog.css';

interface BlogPost {
	id: string;
	title: string;
	date: string;
	category: string;
	excerpt: string;
	content: string;
}

const parseFrontMatter = (raw: string, id: string): BlogPost => {
	const lines = raw.split('\n');
	const meta: Record<string, string> = {};
	let contentStart = 0;
	let inFrontMatter = false;

	for (let i = 0; i < lines.length; i++) {
		if (lines[i].trim() === '---') {
			if (!inFrontMatter) {
				inFrontMatter = true;
				continue;
			} else {
				contentStart = i + 1;
				break;
			}
		}
		if (inFrontMatter) {
			const colonIdx = lines[i].indexOf(':');
			if (colonIdx > -1) {
				const key = lines[i].substring(0, colonIdx).trim();
				const value = lines[i].substring(colonIdx + 1).trim();
				meta[key] = value;
			}
		}
	}

	return {
		id,
		title: meta['title'] || 'Untitled',
		date: meta['date'] || '',
		category: meta['category'] || 'Allgemein',
		excerpt: meta['excerpt'] || '',
		content: lines.slice(contentStart).join('\n').trim(),
	};
};

const BlogDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const [post, setPost] = useState<BlogPost | null>(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		if (!id) {
			setNotFound(true);
			setLoading(false);
			return;
		}
		const loadPost = async () => {
			try {
				const response = await fetch(`/blog-posts/${id}.md`);
				if (!response.ok) {
					setNotFound(true);
					return;
				}
				const raw = await response.text();
				setPost(parseFrontMatter(raw, id));
			} catch {
				setNotFound(true);
			} finally {
				setLoading(false);
			}
		};
		loadPost();
	}, [id]);

	const formatDate = (dateStr: string) =>
		new Date(dateStr).toLocaleDateString('de-DE', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});

	if (loading) {
		return (
			<div>
				<Header style={{ backgroundColor: '#ffffff' }} />
				<div className='blog-page'>
					<div className='blog-container'>
						<div className='blog-loading'>
							<div className='blog-loading-spinner' />
							<p>Beitrag wird geladen…</p>
						</div>
					</div>
				</div>
				<MobileFooter />
			</div>
		);
	}

	if (notFound || !post) {
		return (
			<div>
				<Header style={{ backgroundColor: '#ffffff' }} />
				<div className='blog-page'>
					<div className='blog-container'>
						<div className='blog-not-found'>
							<h1>Beitrag nicht gefunden</h1>
							<p>Der gesuchte Blog-Beitrag existiert nicht oder wurde entfernt.</p>
							<Link to='/blog' className='back-link'>← Zurück zum Blog</Link>
						</div>
					</div>
				</div>
				<MobileFooter />
			</div>
		);
	}

	return (
		<div>
			<Header style={{ backgroundColor: '#ffffff' }} />
			<div className='blog-page'>
				<div className='blog-container blog-detail'>
					<Link to='/blog' className='back-link'>Zurück zum Blog</Link>

					<div className='detail-header'>
						<span className='detail-category'>{post.category}</span>
						<h1 className='detail-title'>{post.title}</h1>
						<p className='detail-date'>{formatDate(post.date)}</p>
					</div>

					<div className='detail-content'>
						<ReactMarkdown rehypePlugins={[rehypeRaw]}>{post.content}</ReactMarkdown>
					</div>
				</div>
			</div>
			<MobileFooter />
		</div>
	);
};

export default BlogDetailPage;

